import { useState, useEffect } from "react";
import { PLAN_TYPE_META } from "../../../common/constants";

const EMPTY_FORM = {
  name: "",
  description: "",
  price: "",
  sessionDurationMinutes: "",
  planType: "GROUP",
  isFree: false,
  isActive: true,
  sessionsGroup: "",
  sessionsGroupDiscussion: "",
  sessionsOneOnOne: "",
  sessionsPartnerPractice: "",
};

// Map planType to its single session sub-field
const SESSION_FIELD = {
  GROUP: "sessionsGroup",
  ONE_ON_ONE: "sessionsOneOnOne",
  PARTNER_PRACTICE: "sessionsPartnerPractice",
};

const PlanForm = ({ onSubmit, onClose, initialData }) => {
  const isEdit = !!initialData;
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        price: initialData.price ?? "",
        sessionDurationMinutes: initialData.sessionDurationMinutes ?? "",
        sessionsGroup: initialData.sessionsGroup ?? "",
        sessionsGroupDiscussion: initialData.sessionsGroupDiscussion ?? "",
        sessionsOneOnOne: initialData.sessionsOneOnOne ?? "",
        sessionsPartnerPractice: initialData.sessionsPartnerPractice ?? "",
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "isFree" && checked ? { price: 0 } : {}),
      ...(name === "isFree" && !checked ? { price: "" } : {}),
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined, sessions: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = "Name is required.";
    if (formData.price === "" || Number(formData.price) < 0)
      errs.price = "Price must be >= 0.";
    if (!formData.sessionDurationMinutes || Number(formData.sessionDurationMinutes) <= 0)
      errs.sessionDurationMinutes = "Duration must be > 0.";

    if (formData.planType === "COMBO") {
      const hasSession =
        Number(formData.sessionsGroup || 0) > 0 ||
        Number(formData.sessionsGroupDiscussion || 0) > 0 ||
        Number(formData.sessionsOneOnOne || 0) > 0 ||
        Number(formData.sessionsPartnerPractice || 0) > 0;
      if (!hasSession) errs.sessions = "At least one session field must be > 0.";
    } else {
      const field = SESSION_FIELD[formData.planType];
      if (!field || !(Number(formData[field] || 0) > 0))
        errs.sessions = "Session count must be > 0.";
    }
    return errs;
  };

  const buildPayload = () => {
    const base = {
      name: formData.name,
      description: formData.description,
      price: Number(formData.price),
      planType: formData.planType,
      sessionDurationMinutes: Number(formData.sessionDurationMinutes),
      isFree: formData.isFree,
    };

    // For non-COMBO: map session count to the correct sub-field, rest are 0
    if (formData.planType !== "COMBO") {
      const field = SESSION_FIELD[formData.planType];
      const sessionCount = Number(formData[field] || 0);
      return {
        ...base,
        totalSessions: sessionCount,
        sessionsGroup: formData.planType === "GROUP" ? sessionCount : 0,
        sessionsGroupDiscussion: 0,
        sessionsOneOnOne: formData.planType === "ONE_ON_ONE" ? sessionCount : 0,
        sessionsPartnerPractice: formData.planType === "PARTNER_PRACTICE" ? sessionCount : 0,
      };
    }

    // COMBO: each sub-field from its own input; backend computes totalSessions
    return {
      ...base,
      totalSessions: 0,
      sessionsGroup: Number(formData.sessionsGroup || 0),
      sessionsGroupDiscussion: Number(formData.sessionsGroupDiscussion || 0),
      sessionsOneOnOne: Number(formData.sessionsOneOnOne || 0),
      sessionsPartnerPractice: Number(formData.sessionsPartnerPractice || 0),
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit(buildPayload());
  };

  // Label for the single session input on non-COMBO plans
  const sessionLabel = {
    GROUP: "Sessions - Group",
    ONE_ON_ONE: "Sessions - One-on-One",
    PARTNER_PRACTICE: "Sessions - Partner Practice",
  };

  return (
    <div className="plan-form-overlay">
      <form onSubmit={handleSubmit} className="plan-form-modal">
        <h3>{isEdit ? "Edit Subscription Plan" : "Add Subscription Plan"}</h3>

        <label>Plan Name</label>
        <input
          name="name"
          placeholder="e.g. Fluency Builder"
          value={formData.name}
          onChange={handleChange}
        />
        {errors.name && <span className="form-error">{errors.name}</span>}

        <label>Description</label>
        <textarea
          name="description"
          placeholder="e.g. 5 group sessions"
          value={formData.description}
          onChange={handleChange}
          rows={3}
        />

        <label>Plan Type</label>
        <select
          name="planType"
          value={formData.planType}
          onChange={handleChange}
          disabled={isEdit}
        >
          {["GROUP", "ONE_ON_ONE", "PARTNER_PRACTICE", "COMBO"].map((type) => (
            <option key={type} value={type}>
              {PLAN_TYPE_META[type]?.displayName ?? type}
            </option>
          ))}
        </select>

        <label>Price (₹)</label>
        <input
          type="number"
          name="price"
          placeholder="e.g. 249"
          value={formData.price}
          onChange={handleChange}
          disabled={formData.isFree}
          min={0}
        />
        {errors.price && <span className="form-error">{errors.price}</span>}

        <label>Session Duration (minutes)</label>
        <input
          type="number"
          name="sessionDurationMinutes"
          placeholder="e.g. 30"
          value={formData.sessionDurationMinutes}
          onChange={handleChange}
          min={1}
        />
        {errors.sessionDurationMinutes && (
          <span className="form-error">{errors.sessionDurationMinutes}</span>
        )}

        {/* Non-COMBO: single session input mapped directly to the sub-field */}
        {formData.planType !== "COMBO" && (
          <>
            <label>{sessionLabel[formData.planType]}</label>
            <input
              type="number"
              name={SESSION_FIELD[formData.planType]}
              value={formData[SESSION_FIELD[formData.planType]]}
              onChange={handleChange}
              min={1}
            />
          </>
        )}

        {/* COMBO: all 4 sub-fields */}
        {formData.planType === "COMBO" && (
          <>
            <label>Sessions - Group</label>
            <input
              type="number"
              name="sessionsGroup"
              value={formData.sessionsGroup}
              onChange={handleChange}
              min={0}
            />
            <label>Sessions - Group Discussion</label>
            <input
              type="number"
              name="sessionsGroupDiscussion"
              value={formData.sessionsGroupDiscussion}
              onChange={handleChange}
              min={0}
            />
            <label>Sessions - One-on-One</label>
            <input
              type="number"
              name="sessionsOneOnOne"
              value={formData.sessionsOneOnOne}
              onChange={handleChange}
              min={0}
            />
            <label>Sessions - Partner Practice</label>
            <input
              type="number"
              name="sessionsPartnerPractice"
              value={formData.sessionsPartnerPractice}
              onChange={handleChange}
              min={0}
            />
          </>
        )}
        {errors.sessions && <span className="form-error">{errors.sessions}</span>}

        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
          <input
            type="checkbox"
            name="isFree"
            checked={formData.isFree}
            onChange={handleChange}
          />
          Is Free Plan
        </label>

        <div className="form-actions">
          <button type="submit">{isEdit ? "Update Plan" : "Save Plan"}</button>
          <button type="button" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default PlanForm;
